import React from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle,
  Logout,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import LanguageSwitcher from './LanguageSwitcher';
import PanneauNotifications from './notifications/PanneauNotifications';
import { useLanguage } from '../contexts/LanguageContext';
import { performLogout, fileUrl } from '../services/api';

const drawerWidth = 216; // 240 * 0.9

// 🔴 LE HEADER EST BLANC (lot RU1) — le dégradé quitte l'AppBar.
//
// Il portait `createGradient(stockColor)` : un dégradé plein largeur, calculé à
// partir de la couleur du stock actif. Deux raisons de l'en retirer :
//   · une barre colorée en permanence est une SURFACE, pas une information ;
//     elle occupe le haut de chaque écran sans jamais rien dire de nouveau ;
//   · la couleur du stock, elle, EST une information — et elle se perdait dans
//     un aplat de la largeur de l'écran.
//
// 🟡 ET ELLE N'EST PLUS AFFICHÉE NULLE PART DANS CE HEADER. Ce n'est pas un
// oubli : le header ne montre PAS le nom du stock actif (vérifié — la barre ne
// contient que le bouton de menu, la langue, les notifications et le compte).
// La couleur ne s'y rattachait donc à rien de nommé. La faire réapparaître
// suppose d'y AJOUTER le stock actif — un changement de contenu, hors du
// périmètre d'un lot qui ne doit rien changer d'autre que l'apparence.
// Consigné au ROADMAP.
//
// ⚠️ `createGradient` a été supprimée : sa forme (135deg, 0 % → 100 %) est celle
// du jeton `degrade()` de `theme/jetons.js`, qui accepte une couleur de départ.
// C'était la septième copie du même dégradé dans le dépôt.
const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  transition: theme.transitions.create(['width', 'margin', 'background'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  marginLeft: 0,
  width: open ? `calc(100% - ${drawerWidth}px)` : '100%',
}));

const Header = ({ open, onMenuClick, unreadCount = 0, user,
                 refreshUnreadNotifications, showSnackbar }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [anchorEl, setAnchorEl] = React.useState(null);
  

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  // 🔴 LA CLOCHE OUVRE UN PANNEAU AU LIEU DE NAVIGUER. Elle n'était pas inerte —
  // elle faisait `navigate('/notifications')` : le badge annonçait un nombre, et
  // le clic **quittait l'écran en cours** pour aller le chercher. *On ne pouvait
  // atteindre ses notifications qu'en abandonnant ce qu'on faisait.*
  // ⚠️ Le lien « Voir toutes les notifications » du panneau garde ce chemin : la
  // navigation n'est pas supprimée, elle cesse d'être le SEUL geste possible.
  const [ancreNotifications, setAncreNotifications] = React.useState(null);

  const handleNotificationsClick = (event) => {
    setAncreNotifications(event.currentTarget);
  };

  const handleProfileClick = () => {
    handleProfileMenuClose();
    navigate('/profile');
  };

  const handleLogoutClick = async () => {
    handleProfileMenuClose();
    // Déconnexion : unset des cookies côté serveur + purge localStorage, puis retour accueil.
    await performLogout();
    window.location.href = '/';
  };

  return (
    <StyledAppBar position="fixed" open={open}>
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label={open ? "fermer le menu" : "ouvrir le menu"}
          onClick={onMenuClick}
          edge="start"
          sx={{
            marginRight: 2,
            transition: 'transform 0.3s ease-in-out',
            '&:hover': {
              transform: 'scale(1.1)',
            },
          }}
        >
          <MenuIcon />
        </IconButton>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Bouton de langue */}
          <LanguageSwitcher />

          {/* Notifications */}
          <IconButton
            color="inherit"
            onClick={handleNotificationsClick}
            sx={{ ml: 1 }}
          >
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* Menu utilisateur */}
          <IconButton
            size="large"
            edge="end"
            aria-label="compte utilisateur"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            {/* 🔴 CES VALEURS ÉTAIENT PENSÉES POUR L'ANCIEN HEADER EN DÉGRADÉ
                VIOLET, et elles lui ont survécu. `bgcolor:
                'rgba(255,255,255,0.15)'` — du blanc à 15 % — se lisait sur du
                violet ; sur un header devenu BLANC, l'avatar disparaissait
                purement et simplement, initiales comprises.
                ⚠️ ET LE MODE SOMBRE NE POUVAIT PAS LE RÉVÉLER : sur un fond
                gris, du blanc à 15 % fonctionne encore, par coïncidence. Le
                défaut n'existait qu'en mode CLAIR — le miroir exact de
                `#f8fafc`, qui n'existait qu'en mode sombre.
                La couleur vient donc de la palette, et le contraste du texte
                est calculé par MUI (`contrastText`) plutôt que supposé. */}
            <Avatar
              sx={{
                width: 28.8,
                height: 28.8,
                fontSize: '0.8125rem',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
              src={
                user?.avatar_path
                  ? fileUrl(user.avatar_path)
                  : undefined
              }
            >
              {!user?.avatar_path &&
                `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase()}
            </Avatar>
          </IconButton>
        </Box>

        {/* Menu déroulant utilisateur */}
        <Menu
          id="primary-search-account-menu"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
        >
          <MenuItem onClick={handleProfileClick}>
            <AccountCircle sx={{ mr: 2 }} />
            {t('common.profile')}
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogoutClick}>
            <Logout sx={{ mr: 2 }} />
            {t('navigation.logout')}
          </MenuItem>
        </Menu>
      </Toolbar>
      {/* 🔴 LE PANNEAU EST MONTÉ ICI, dans l'entête présent sur TOUS les écrans.
          Il ne charge rien tant qu'il n'est pas ouvert : monté partout, un
          chargement au montage ferait une requête par navigation, pour une
          liste que personne ne regarde.
          ⚠️ `onChangement` rafraîchit le BADGE : marquer tout comme lu depuis le
          panneau doit faire tomber le compteur de la cloche, sinon l'écran
          contredit le geste qu'on vient de faire. */}
      <PanneauNotifications
        ancre={ancreNotifications}
        onFermer={() => setAncreNotifications(null)}
        onNaviguer={navigate}
        onChangement={refreshUnreadNotifications}
        showSnackbar={showSnackbar}
      />
    </StyledAppBar>
  );
};

export default Header; 